// components/finance/sections/AMIBreakdown.jsx

import React, { useState, useMemo } from 'react';
import { 
  ChartBarIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';
import MetricCard from '../../common/MetricCard';
import { StackedBarChart } from '../../common/StackedBarChart';
import { useAMIBreakdown } from '../../../hooks/finance/useAMIBreakdown';
import { formatCurrency } from '../../../utils';

// Helper function to calculate averages for a specific year
const calculateYearAverages = (monthlyBreakdown, targetYear) => {
  if (!monthlyBreakdown || monthlyBreakdown.length === 0) {
    return {
      gross_profit_total: 0,
      labor_total: 0,
      overhead_total: 0,
      profit_total: 0,
      labor_costs_pct: 0,
      overhead_pct: 0,
      profit_pct: 0,
      count: 0,
    };
  }

  // Filter for target year's data
  const yearData = monthlyBreakdown.filter(month => {
    const year = parseInt(month.period_label.split(' ')[1], 10);
    return year === targetYear;
  });

  if (yearData.length === 0) {
    return {
      gross_profit_total: 0,
      labor_total: 0,
      overhead_total: 0,
      profit_total: 0,
      labor_costs_pct: 0,
      overhead_pct: 0,
      profit_pct: 0,
      count: 0,
    };
  }

  // Calculate averages
  const sum = yearData.reduce((acc, month) => ({
    gross_profit_total: acc.gross_profit_total + (month.gross_profit || 0),
    labor_total: acc.labor_total + (month.labor_costs || 0),
    overhead_total: acc.overhead_total + (month.overhead || 0),
    profit_total: acc.profit_total + (month.profit || 0),
  }), { gross_profit_total: 0, labor_total: 0, overhead_total: 0, profit_total: 0 });
  
  return {
    labor_costs_pct: sum.gross_profit_total > 0 ? (sum.labor_total / sum.gross_profit_total * 100) : 0,
    overhead_pct: sum.gross_profit_total > 0 ? (sum.overhead_total / sum.gross_profit_total * 100) : 0,
    profit_pct: sum.gross_profit_total > 0 ? (sum.profit_total / sum.gross_profit_total * 100) : 0,
    count: yearData.length,
  };
};

export const AMIBreakdown = ({ dateRange }) => {
  // Use the AMI breakdown hook with dateRange
  const {
    goals,
    period_averages,
    monthly_breakdown,
    period,
    loading,
    error,
    refetch,
  } = useAMIBreakdown(dateRange);

  // console.log("dateRange==", dateRange);
  // console.log("monthly_breakdown==", monthly_breakdown);

  const [showBreakdownTable, setShowBreakdownTable] = useState(false);
  const [sortConfig, setSortConfig] = useState({ 
    key: 'period_date',
    direction: 'desc' 
  });

  // Create period label with year (matching FinancialHealth pattern)
  const periodLabel = useMemo(() => {
    if (!dateRange?.start_date) return 'YTD';
    
    const selectedYear = parseInt(dateRange.start_date.split('-')[0], 10);
    const currentYear = new Date().getFullYear();
    const endDate = dateRange.end_date;
    const endOfYear = `${selectedYear}-12-31`;
    
    const isYTD = endDate < endOfYear;
    
    if (selectedYear === currentYear) {
      return `${selectedYear} YTD`;
    }
    
    if (!isYTD) {
      return `${selectedYear}`;
    }
    
    return `${selectedYear} YTD`;
  }, [dateRange]);

  const formattedDateRange = useMemo(() => {
    if (!dateRange?.start_date || !dateRange?.end_date) {
      return null;
    }
    
    const formatDate = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    
    return `${formatDate(dateRange.start_date)} - ${formatDate(dateRange.end_date)}`;
  }, [dateRange]);

  // Parse period label to Date for proper sorting
  const parsePeriodToDate = (periodLabel) => {
    try {
      const [month, year] = periodLabel.split(' ');
      const monthIndex = new Date(Date.parse(month + " 1, 2000")).getMonth();
      return new Date(parseInt(year), monthIndex, 1);
    } catch (e) {
      return new Date(0);
    }
  };

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Get the most recent year from the monthly_breakdown data
  const getYearsFromData = (monthlyBreakdown) => {
    if (!monthlyBreakdown || monthlyBreakdown.length === 0) {
      return { currentYear: new Date().getFullYear(), hasData: false };
    }
    
    const years = monthlyBreakdown.map(month => {
      const year = parseInt(month.period_label.split(' ')[1], 10);
      return year;
    });
    
    const maxYear = Math.max(...years);
    return { currentYear: maxYear, hasData: true };
  };

  const { currentYear } = getYearsFromData(monthly_breakdown);

  // Calculate current year (YTD) averages from monthly_breakdown
  const currentYearAverages = calculateYearAverages(monthly_breakdown, currentYear);

  // Handle loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 p-4">
          {[1, 2, 3].map((i) => (
            <MetricCard key={i} loading={true} />
          ))}
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        <ExclamationTriangleIcon className="h-5 w-5 inline mr-2" />
        Error loading AMI data: {error}
        <button 
          onClick={refetch}
          className="ml-4 text-sm underline hover:text-red-900"
        >
          Retry
        </button>
      </div>
    );
  }

  // Handle no data state
  if (!monthly_breakdown || monthly_breakdown.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-500 text-center">
        No AMI breakdown data available
      </div>
    );
  }

  // Configure the stacked bar chart using currentYearAverages
  const stackedBarConfig = {
    segments: [
      { 
        key: 'labor', 
        label: 'Labor Costs', 
        color: 'bg-brightblue-500', 
        textColor: '',
        target: goals.labor_costs
      },
      { 
        key: 'overhead', 
        label: 'Expenses/Overhead', 
        color: 'bg-violet-500', 
        textColor: '',
        target: goals.overhead
      },
      { 
        key: 'profit', 
        label: 'Profit', 
        color: 'bg-green-500', 
        textColor: '',
        target: goals.profit
      }
    ],
    targets: [
      { value: goals.labor_costs, label: `${goals.labor_costs}%` },
      { value: goals.labor_costs + goals.overhead, label: `${goals.labor_costs + goals.overhead}%` }
    ],
    legend: [
      { color: 'bg-brightblue-500', label: 'Labor Costs' },
      { color: 'bg-violet-500', label: 'Overhead' },
      { color: 'bg-green-500', label: 'Profit' }
    ]
  };

  // Prepare data for the stacked bar chart using currentYearAverages
  const stackedBarData = {
    labor: currentYearAverages.labor_costs_pct || 0,
    overhead: currentYearAverages.overhead_pct || 0,
    profit: currentYearAverages.profit_pct || 0
  };

  // Sort monthly breakdown
  const sortedBreakdown = monthly_breakdown && Array.isArray(monthly_breakdown)
    ? [...monthly_breakdown].sort((a, b) => {
        let aValue, bValue;
        
        if (sortConfig.key === 'period_date') {
          aValue = parsePeriodToDate(a.period_label);
          bValue = parsePeriodToDate(b.period_label);
        } else if (sortConfig.key === 'period_label') {
          aValue = parsePeriodToDate(a.period_label);
          bValue = parsePeriodToDate(b.period_label);
        } else {
          aValue = a[sortConfig.key] || 0;
          bValue = b[sortConfig.key] || 0;
        }
        
        if (aValue instanceof Date && bValue instanceof Date) {
          return sortConfig.direction === 'asc' 
            ? aValue - bValue
            : bValue - aValue;
        }
        
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      })
    : [];

  // Calculate totals for the period
  const calculateTotals = (breakdown) => {
    if (!breakdown || breakdown.length === 0) {
      return {
        total_income: 0,
        gross_profit: 0,
        labor_costs: 0,
        overhead: 0,
        profit: 0,
        net_income: 0,
        labor_costs_pct: 0,
        overhead_pct: 0,
        profit_pct: 0,
        net_income_pct: 0,
      };
    }

    const totals = breakdown.reduce((acc, month) => ({
      total_income: acc.total_income + (month.total_income || 0),
      gross_profit: acc.gross_profit + (month.gross_profit || 0),
      labor_costs: acc.labor_costs + (month.labor_costs || 0),
      overhead: acc.overhead + (month.overhead || 0),
      profit: acc.profit + (month.profit || 0),
      net_income: acc.net_income + (month.net_income || 0),
    }), {
      total_income: 0,
      gross_profit: 0,
      labor_costs: 0,
      overhead: 0,
      profit: 0,
      net_income: 0,
    });

    const gross_profit_total = totals.gross_profit || 0;
    
    return {
      ...totals,
      labor_costs_pct: gross_profit_total > 0 ? (totals.labor_costs / gross_profit_total) * 100 : 0,
      overhead_pct: gross_profit_total > 0 ? (totals.overhead / gross_profit_total) * 100 : 0,
      profit_pct: gross_profit_total > 0 ? (totals.profit / gross_profit_total) * 100 : 0,
      net_income_pct: gross_profit_total > 0 ? (totals.net_income / gross_profit_total) * 100 : 0,
    };
  };

  const periodTotals = calculateTotals(sortedBreakdown);

  return (
    <div className="space-y-6">
      <div 
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 cursor-pointer hover:shadow-md transition-all"
        onClick={() => setShowBreakdownTable(!showBreakdownTable)}
      >
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900">Breakdown (55/25/20)</h3>
          <p className="text-sm text-gray-600 mt-1">
            Profit First Allocation - {periodLabel}
          </p>
        </div>

        <StackedBarChart
          data={stackedBarData}
          config={stackedBarConfig}
          showDetails={true}
          showLegend={true}
        />

        {/* Arrow toggle indicator */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="text-xs text-gray-600 flex items-center justify-end gap-2">
            <span>Click to {showBreakdownTable ? 'hide' : 'view'} monthly details</span>
            <div className="group flex items-center justify-center h-6 w-6 rounded-full border-2 border-gray-400 hover:border-primary-500 hover:bg-primary-500 hover:scale-110 transition-all cursor-pointer">
              {showBreakdownTable ? (
                <ArrowUpIcon className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
              ) : (
                <ArrowDownIcon className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Breakdown Table */}
      {showBreakdownTable && sortedBreakdown.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Monthly Breakdown</h3>
            <p className="text-sm text-gray-600 mt-1">
              {formattedDateRange || `${period.start_date} to ${period.end_date}`} ({sortedBreakdown.length} months)
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-brightblue-500">
                <tr>
                  {[
                    { key: 'period_label', label: 'Month', align: 'left' },
                    { key: 'total_income', label: 'Total Income', align: 'center' },
                    { key: 'gross_profit', label: 'Gross Profit', align: 'center' },
                    { key: 'labor_costs', label: 'Labor Costs $', align: 'center' },
                    { key: 'overhead', label: 'Overhead $', align: 'center' },
                    { key: 'profit', label: 'Profit $', align: 'center' },
                    { key: 'net_income', label: 'Net Income $', align: 'center' },
                    { key: 'labor_costs_pct', label: 'Labor %', align: 'center' },
                    { key: 'overhead_pct', label: 'Overhead %', align: 'center' },
                    { key: 'profit_pct', label: 'Profit %', align: 'center' },
                    { key: 'net_income_pct', label: 'Net Income %', align: 'center' }
                  ].map(({ key, label, align }) => (
                    <th 
                      key={key}
                      className={`px-6 py-3 text-${align} text-sm font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-brightblue-600`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSort(key);
                      }}
                    >
                      <div className={`flex items-center ${align === 'center' ? 'justify-center' : ''} space-x-1`}>
                        <span>{label}</span>
                        {sortConfig.key === key && (
                          sortConfig.direction === 'asc' ? 
                            <ChevronUpIcon className="w-4 h-4" /> : 
                            <ChevronDownIcon className="w-4 h-4" />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedBreakdown.map((month, index) => {
                  // Calculate on-target status in frontend
                  const laborOnTarget = (month.labor_costs_pct || 0) <= goals.labor_costs;
                  const overheadOnTarget = (month.overhead_pct || 0) <= goals.overhead;
                  const profitOnTarget = (month.profit_pct || 0) >= goals.profit;
                  
                  return (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                        {month.period_label}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center font-medium text-gray-900">
                        {formatCurrency(month.total_income)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-gray-900">
                        {formatCurrency(month.gross_profit || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-gray-900">
                        {formatCurrency(month.labor_costs || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-gray-900">
                        {formatCurrency(month.overhead || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-gray-900">
                        {formatCurrency(month.profit || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-gray-900">
                        {formatCurrency(month.net_income || 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                          laborOnTarget ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {month.labor_costs_pct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                          overheadOnTarget ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {month.overhead_pct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                          profitOnTarget ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {month.profit_pct.toFixed(1)}%
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded`}>
                          {month.net_income_pct.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
                
                {/* TOTALS ROW */}
                <tr className="bg-brightblue-50 border-t-2 border-brightblue-500 font-semibold">
                  <td className="px-6 py-4 whitespace-nowrap font-bold text-gray-900">
                    PERIOD TOTAL
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-gray-900">
                    {formatCurrency(periodTotals.total_income)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-gray-900">
                    {formatCurrency(periodTotals.gross_profit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-gray-900">
                    {formatCurrency(periodTotals.labor_costs)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-gray-900">
                    {formatCurrency(periodTotals.overhead)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-gray-900">
                    {formatCurrency(periodTotals.profit)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-gray-900">
                    {formatCurrency(periodTotals.net_income)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-bold rounded ${
                      periodTotals.labor_costs_pct <= goals.labor_costs 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {periodTotals.labor_costs_pct.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-bold rounded ${
                      periodTotals.overhead_pct <= goals.overhead 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {periodTotals.overhead_pct.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-bold rounded ${
                      periodTotals.profit_pct >= goals.profit 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {periodTotals.profit_pct.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-2 py-1 text-xs font-bold rounded`}>
                      {periodTotals.net_income_pct.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No data state */}
      {sortedBreakdown.length === 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No monthly breakdown data available</p>
        </div>
      )}
    </div>
  );
};

export default AMIBreakdown;